import type {
  ShipmentCurrentExtras,
  PieceCurrentExtras,
} from "./api/shipment-current";
// ============================================================================
// Core Types - Customer-facing subset from ezx-server schema
// ============================================================================

// Generic Types
export interface PaginatedResponse<T> {
  data: T[];
  metadata?: {
    total: number;
    page: number;
    limit: number;
    totalPages?: number;
  };
}

// Enums
export enum CustomerType {
  INDIVIDUAL = "INDIVIDUAL",
  BUSINESS = "BUSINESS",
}

export enum ShipmentStatus {
  PENDING_PAYMENT = "PENDING_PAYMENT",
  LABEL_READY = "LABEL_READY",
  DROPPED_AT_CARRIER = "DROPPED_AT_CARRIER",
  PICKED_UP = "PICKED_UP",
  IN_TRANSIT = "IN_TRANSIT",
  AT_FACILITY = "AT_FACILITY",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED = "DELIVERED",
  AVAILABLE_FOR_PICKUP = "AVAILABLE_FOR_PICKUP",
  DELIVERY_ATTEMPTED = "DELIVERY_ATTEMPTED",
  EXCEPTION = "EXCEPTION",
  ON_HOLD = "ON_HOLD",
  RETURNED = "RETURNED",
  CANCELLED = "CANCELLED",
}

export enum OrderStatus {
  REGISTERED = "REGISTERED",
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
}

export enum Provider {
  LOCAL = "LOCAL",
  SHIPSTATION = "SHIPSTATION",
  CHITCHATS = "CHITCHATS",
  SHIPENGINE = "SHIPENGINE",
  EASYPOST = "EASYPOST",
  DIRECT_CP = "DIRECT_CP",
  DIRECT_UPS = "DIRECT_UPS",
  DIRECT_FEDEX = "DIRECT_FEDEX",
  DIRECT_USPS = "DIRECT_USPS",
  CUSTOM = "CUSTOM",
}

export enum PaymentMethodType {
  SAVED_CARD = "SAVED_CARD",
  PAYMENT_LINK = "PAYMENT_LINK",
  CREDIT = "CREDIT",
  INVOICE = "INVOICE",
  CASH = "CASH",
  E_TRANSFER = "E_TRANSFER",
  WECHAT = "WECHAT",
}

// ============================================================================
// Core Models
// ============================================================================

export interface Address {
  plusCode?: string | null;
  id: string;
  line1: string;
  line2?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  location?: {
    lat: number;
    lng: number;
  };
  isValidated?: boolean;
  validatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  name?: string;
  phone?: string;
  picture?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Customer {
  id: string;
  customerNumber: number;
  type: CustomerType;
  name: string;
  email?: string;
  phone?: string;
  businessName?: string;
  businessNumber?: string;
  status: string;
  billing?: CustomerBilling;
  defaultAddressId?: string;
  defaultAddress?: Address;
  userId?: string;
  user?: User;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerBilling {
  id: string;
  customerId?: string;
  stripeCustomerId?: string;
  defaultPaymentMethodId?: string;
  billingEmail?: string;
  billingAddressId?: string;
  billingAddress?: Address;
  autoCharge?: boolean;
  creditLimit?: number;
  creditUsed?: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Order & Shipment Models
// ============================================================================

export interface Order {
  id: string;
  orderNumber: number;
  placerCustomerId?: string;
  placerCustomer?: Customer;

  createdAt: string;
  updatedAt: string;

  /** Completed payments allocated to this Order, before refunds. Includes separate pickup purchases. */
  totalPaidCents?: number;
  separatePickupPaidCents?: number;

  refundSummary?: {
    settledCashCents: number;
    settledCreditCents: number;
    settlementState: "NONE" | "PENDING" | "PROCESSING" | "FAILED" | "COMPLETED";
  };

  // Payment fields
  paymentStatus?:
    | "PENDING"
    | "PROCESSING"
    | "PAID"
    | "PARTIAL"
    | "REFUNDED"
    | "PARTIAL_REFUND"
    | "FAILED"
    | "CREDIT_APPROVED"
    | "VOIDED";
  paymentMethodType?: PaymentMethodType;
  paymentTerms?: string | null;
  paymentTermsId?: string | null;
  subtotalCents?: number;
  taxCents?: number;
  totalCents?: number;
  paidAmountCents?: number;
  balanceCents?: number;

  metadata?: any;

  shipments?: Shipment[];
}

export interface TimeSlot {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

export interface Shipment extends ShipmentCurrentExtras {
  id: string;
  orderId: string;
  order?: Order;
  shipmentNumber?: number;

  carrierId: string;
  providerRef?: string;
  status: ShipmentStatus;
  /**
   * Set when this shipment is attached to a pickup booking (we collect from
   * sender). Null/undefined = sender drops off (to us, or to a carrier).
   */
  pickupBookingId?: string | null;

  sellAmountCents?: number;

  carrierCode?: string;
  serviceCode?: string;

  trackingNumber?: string;
  trackingUrl?: string;

  /** Customer-entered delivery instructions/notes for this shipment. */
  deliveryNotes?: string;

  shipDate?: string;
  estimatedDelivery?: string;
  actualDelivery?: string;

  /** Server includes the related pickup booking (with slot) on customer-orders response. */
  pickupBooking?: {
    id: string;
    scheduledDate: string;
    cancelledAt?: string | null;
    slot?: TimeSlot;
    notes?: string | null;
    dispatchCenter?: {
      id: string;
      name: string;
      /** IANA tz the customer-facing date display should anchor on. */
      timeZone: string;
    } | null;
  } | null;
  returnTransports?: ReturnTransportSummary[];
  deliveryDate?: string;

  totalWeight?: number;
  metadata?: any;

  addresses?: ShipmentAddress[];

  packages?: ShipmentPackage[];
  tasks?: ShipmentTask[];

  cancellationRequestedAt?: string;

  createdAt: string;
  updatedAt: string;
}

export type ReturnTransportStatus =
  | "QUOTED"
  | "ACCEPTED"
  | "SETTLED"
  | "AMOUNT_DUE"
  | "FAILED";

export type ReturnTransportPayer = "CUSTOMER" | "CARRIER" | "EZX" | "WAIVED";

export interface ReturnTransportSummary {
  id: string;
  status: ReturnTransportStatus;
  settlementState:
    | "PENDING"
    | "PROCESSING"
    | "MANUAL_PAYOUT_PENDING"
    | "COMPLETED"
    | "FAILED";
  payer: ReturnTransportPayer;
  waivedCostBearer: "CARRIER" | "EZX" | null;
  currency: string;
  effectiveNetCents: number;
  taxCents: number;
  totalChargeCents: number;
  grossRefundCents: number | null;
  netCashMovementCents: number | null;
  amountDueCents: number | null;
  refundPaymentId: string | null;
  chargePaymentId: string | null;
  amountDuePaymentId: string | null;
  acceptedAt: string | null;
  settledAt: string | null;
  settlementError: string | null;
  createdAt: string;
  refundRequest: {
    id: string;
    status: "PENDING" | "APPROVED" | "PARTIALLY_APPROVED" | "DENIED";
    totalRefundableCents: number;
    approvedTotalCents: number | null;
    settledAt: string | null;
    settlementError: string | null;
    manualPayouts: Array<{
      id: string;
      status: "AWAITING_PAYOUT" | "PAID" | "FAILED" | "RECONCILIATION_NEEDED";
    }>;
  } | null;
}

export interface ShipmentAddress {
  id: string;
  shipmentId: string;
  addressId?: string;
  address?: Address | null;
  type: "FROM" | "TO" | "RETURN";

  name?: string;
  company?: string;
  phone?: string;
  email?: string;

  // API also returns contactName/contactPhone/contactEmail on some endpoints
  contactName?: string;
  contactCompany?: string;
  propertyResidential?: boolean;
  contactPhone?: string;
  contactEmail?: string;

  // Denormalized address snapshot — the source of truth for newer orders.
  // The optional `address` relation above only exists for legacy orders
  // where the row was linked to a separate Address record.
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressProvince?: string;
  addressCountry?: string;
  addressPostalCode?: string;

  isValidated: boolean;
  isResidential: boolean;
  instructions?: string;

  createdAt: string;
  updatedAt: string;
  addressPlusCode?: string | null;
  addressLocation?: { lat: number; lng: number } | null;
}

export interface ShipmentTask {
  id: string;
  type: string;
  status: string;
  contactName?: string;
  contactPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressProvince?: string;
  addressCountry?: string;
  addressPostalCode?: string;
  proof?: {
    mediaItems?: {
      id: string;
      url: string;
      thumbnailUrl?: string | null;
      type: string;
      key: string;
    }[];
  };
}

export interface ShipmentPackage extends PieceCurrentExtras {
  id: string;
  shipmentId: string;
  index: number;
  weightGrams: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  declaredValueCents?: number;
  packageKind?: string | null;
  weightOriginal?: string | null;
  weightOriginalUnit?: string | null;
  lengthOriginal?: string | null;
  widthOriginal?: string | null;
  heightOriginal?: string | null;
  dimensionOriginalUnit?: string | null;
  packageTypeId?: string | null;

  individualTrackingNumber?: string;
  labelUrl?: string;

  isFragile: boolean;
  requiresCooling: boolean;
  description?: string;

  qrcode?: string;
  qrcodeData?: string;
}

export interface ShipmentQuote {
  id: string;
  shipmentId?: string;
  provider: Provider;
  carrierCode?: string;
  serviceCode?: string;
  serviceName?: string;

  listRate: number;
  retailRate: number;
  currency: string;

  estimatedDays?: number;
  estimatedDelivery?: string;

  metadata?: any;
  expiresAt?: string;

  createdAt: string;
}

export interface PaymentMethod {
  id: string;
  type: string;
  last4: string;
  brand?: string;
  isDefault: boolean;
  expiryMonth?: number;
  expiryYear?: number;
}

export interface PaymentTerms {
  id: string;
  name: string;
  netDays: number;
  isActive: boolean;
}

// #262 — voucher wallet primitive. Customer-side type for
// `GET /api/v2/customers/me/credits` rows.
export type CustomerCreditKind = "AMOUNT" | "FREE_LINE_ITEM";
export type CustomerCreditScope = "PICKUP" | "DELIVERY";
export type CustomerCreditSourceKind =
  | "TIER_PERK"
  | "SERVICE_RECOVERY"
  | "ADMIN_MANUAL"
  | "SLA_MISS"
  | "VOLUME_REBATE"
  | "PROMOTION_RULE";

export interface CustomerCredit {
  id: string;
  customerId: string;
  carrierId: string | null;
  ruleId: string | null;
  kind: CustomerCreditKind;
  scope: CustomerCreditScope | null;
  amountCents: number | null;
  validFrom: string;
  validUntil: string | null;
  redeemedAt: string | null;
  redeemedOrderId: string | null;
  redemptionCents: number | null;
  reason: string;
  sourceKind: CustomerCreditSourceKind;
  issuedAt: string;
  redeemedOrder?: { id: string; orderNumber: number | null } | null;
}

export interface CustomerCreditsSummary {
  amountCents: number;
  freeLineItemCount: { PICKUP: number; DELIVERY: number };
}

// ============================================================================
// API Request/Response Types
// ============================================================================

// Quote calculation request
export interface QuoteRequest {
  /** Resolve an owned saved origin using its authoritative stored point. */
  shipFromAddressBookEntryId?: string;
  /** Pin an owned existing pickup visit when pricing an attachment. */
  pickupBookingId?: string;
  /** Reoffer pickup for a chosen date while retaining the base label terms. */
  pickupDate?: string;
  baseQuoteId?: string;
  customerId?: string;

  // Sender
  senderName: string;
  senderEmail?: string;
  senderPhone?: string;
  shipFrom: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    latitude?: number;
    longitude?: number;
  };

  // Recipient
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  shipTo: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    latitude?: number;
    longitude?: number;
  };

  // Packages with flexible units (server handles conversion)
  packages: {
    weight: number;
    weightUnit: "g" | "kg" | "lb" | "oz";
    length: number;
    width: number;
    height: number;
    dimensionUnit: "cm" | "in";
    packageKind?: "PARCEL" | "THICK_ENVELOPE";
    declaredValueCents?: number;
    isFragile?: boolean;
    requiresCooling?: boolean;
    description?: string;
  }[];

  // Options
  pickupNotes?: string;
  deliveryNotes?: string;
}

/**
 * Pricing-redesign: option from /api/v2/pricing/get-quote. Label and pickup
 * prices split so pickup can be displayed as a separate add-on. Customer
 * echoes back the chosen option's identity in POST /orders for drift detection.
 */
/**
 * Delivery estimate for one pickup slot — FORECAST, not a promise. Always
 * render with "Est." prefixed copy. Null = config incomplete server-side;
 * fall back to the plain "N business days" display.
 */
export interface SlotDeliveryEstimate {
  /** "YYYY-MM-DD" dispatch-local date this estimate assumes. */
  forDate: string;
  /** ISO — when the package is back at dispatch, ready for a run. */
  readyAt: string;
  /** External carriers only: when the package reaches the carrier. ISO datetimes. */
  atCarrier?: { fastest: string; slowest: string };
  /** "datetime" → ISO instants (EZX local delivery); "date" → "YYYY-MM-DD"
   *  calendar dates (external carrier transit). */
  delivered: { fastest: string; slowest: string };
  deliveredPrecision: "datetime" | "date";
}

export type EstimateUnavailableReason =
  | "INCOMPATIBLE_INDUCTION_PLAN"
  | "MISSING_DISPATCH_CONFIGURATION"
  | "MISSING_HANDOFF_CONFIGURATION"
  | "MISSING_SERVICE_DELIVERY_DAYS"
  | "MISSED_INDUCTION_DAY"
  | "NO_DELIVERY_OPPORTUNITY"
  | "NO_BOOKABLE_SLOT";

export interface OptionEstimateReasons {
  withPickup: EstimateUnavailableReason | null;
  withoutPickup: EstimateUnavailableReason | null;
}

export interface WithoutPickupEstimate {
  dispatchCenterId?: string;
  timeZone?: string;
  handoff:
    | { type: "drop_at_carrier" }
    | { type: "bring_to_dispatch"; handoffBy: string };
  delivered: { fastest: string; slowest: string };
  deliveredPrecision: "datetime" | "date";
}

/** Option-level conditional estimate lines (server-computed). */
export interface OptionEstimates {
  /** "Book pickup before {bookByTime} → est. delivery {fastest} – {slowest}".
   *  `delivered` is the recommended slot's full range; precision follows the
   *  same datetime/date convention as SlotDeliveryEstimate. */
  withPickup: {
    bookByTime: string;
    delivered: { fastest: string; slowest: string };
    deliveredPrecision: "datetime" | "date";
  } | null;
  /** The no-pickup conditional, shown when "Include pickup" is OFF. */
  withoutPickup: WithoutPickupEstimate | null;
}

export interface PickupTierOption {
  /** The center selected for this pickup service, independent of other tiers. */
  dispatch: { id: string; timeZone: string; city: string | null };
  pickupServiceId: string;
  pickupServiceCode: string;
  pickupServiceName: string;
  pickupPriceCents: number;
  /** Output tax on this pickup leg at the sender's origin province rate.
   *  Charge if this tier is selected = pickupPriceCents + taxCents. */
  taxCents: number;
  /** Exact tax fraction applied (e.g. 0.13). Use this — not a rate derived
   *  from the rounded taxCents — when recomputing tax on a discounted pickup.
   *  Optional for quotes cached before the field existed. */
  taxFraction?: number;
  timeSlots: PickupTimeSlot[];
}

/**
 * What a customer is told about a carrier discount: that it applied, and by how
 * much. Nothing else.
 *
 * This type used to also declare `baseFreightCents`, `markupCents`,
 * `processingFeeCents`, `discountedFreightCents` and `finalLineCents` — EZX's
 * whole margin breakdown — as non-optional fields. The server strips every one
 * of them for non-admin callers (`presentCarrierDiscount` is an allowlist), so
 * they arrived as `undefined` despite the type promising a number, and the only
 * thing the declaration achieved was inviting someone to render the platform's
 * cut in a customer's browser.
 *
 * `discountedFreightCents` is withheld deliberately too: with the percentage it
 * lets you solve back to the carrier's base rate, and from there EZX's cut.
 */
export interface CarrierDiscountOption {
  source: "override" | "tier" | "none";
  discountPercentage: number;
  /** Actual saving already in labelPriceCents; absent on older cached quotes. */
  appliedDiscountCents?: number;
}

export interface PricingOption {
  pickupRejectionReasons?: Array<{
    pickupServiceId?: string;
    pickupServiceName?: string;
    dateAvailability?: {
      date: string;
      status: "FULL" | "CLOSED" | "CUTOFF" | "UNAVAILABLE";
      nextAvailableDate: string | null;
    };
    packageIssue?: QuotePackageIssue;
    code: string;
    reason: string;
    suggestion?: string;
  }>;
  carrier: {
    id: string;
    code: string;
    name: string;
    logoUrl?: string | null;
  };
  carrierService: {
    id: string;
    code: string;
    name: string;
    estimatedDays?: number;
  };
  /** Service-area IDs resolved server-side; echo back unchanged. */
  fromServiceAreaId: string;
  toServiceAreaId: string;
  labelPriceCents: number;
  carrierDiscount?: CarrierDiscountOption;
  /** Output tax on the label leg at the sender's origin province rate.
   *  Charge for this option = labelPriceCents + taxCents (+ pickup tier if added). */
  taxCents: number;
  /** Per-tier pickup prices. Empty array = pickup unavailable for this option. */
  pickupOptions: PickupTierOption[];
  /** Common-center summary only. Selected pickup context lives on each tier. */
  dispatch: { id: string; timeZone: string; city: string | null } | null;
  /** Delivery estimates (FORECASTS). Null = unavailable (config incomplete) —
   *  show only the existing "N business days" text. Per-slot estimates live on
   *  pickupOptions[].timeSlots[].estimate. */
  estimates: OptionEstimates | null;
  estimateReasons?: OptionEstimateReasons | null;
  currency: "CAD";
  expiresAt: string;
  /**
   * ⚠️ THE ONLY THING AN ORDER MAY CARRY. Not a price — an id.
   *
   * `POST /orders` bills what the SERVER wrote against this id. It used to take
   * `labelPriceCents` from the request body, so a client could send `1` for a $20
   * parcel and be charged a penny (#533). The price is no longer in the request at
   * all, which is why forging this id is pointless: the price is not in it.
   *
   * `labelPriceCents` above is still here — but it is for DISPLAY ONLY. Never echo
   * it back on an order.
   *
   * `purchaseHandle` is GONE. It is the token that BUYS the label, and it used to
   * make a round trip through the browser. It now never leaves the server.
   */
  quoteId: string;
}

export type PickupServiceDay =
  | "MON"
  | "TUE"
  | "WED"
  | "THU"
  | "FRI"
  | "SAT"
  | "SUN";

export type PickupAvailabilityStatus =
  | "AVAILABLE"
  | "CLOSED"
  | "FULL"
  | "CUTOFF";

/** Pickup time slot returned from pricing API. */
export interface PickupTimeSlot {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  serviceDays: PickupServiceDay[];
  /** Minutes before slot start when bookings stop accepting. Mirrors the
   *  server-side cutoff so the UI can filter past-cutoff slots without
   *  letting the customer pick one that will be rejected on submit. */
  bookingLeadMinutes: number;
  sortOrder?: number;
  /** Remaining capacity for the selected pickup date, or null when the slot
   *  is uncapped. Absent on the quote response (date unknown at quote time);
   *  populated by the /pricing/slot-availability lookup once a date is picked. */
  remaining?: number | null;
  /** Delivery estimate for this slot (computed for the quote's earliest
   *  bookable date; refreshed per chosen date by /pricing/slot-availability).
   *  Null = unavailable. */
  estimate?: SlotDeliveryEstimate | null;
  estimateReason?: EstimateUnavailableReason | null;
}

/**
 * Echo of the chosen `PricingOption` in POST /orders shipments[i]. Server
 * recomputes via composer; >5% drift returns 409 PRICING_DRIFT with new prices.
 */
export interface SelectedRate {
  /**
   * ⚠️ THE ORDER CARRIES A QUOTE ID, NOT A PRICE (#533).
   *
   * `labelPriceCents` and `purchaseHandle` used to live here. The server billed
   * the price straight from this body — so a client could send `1` for a $20
   * parcel and pay a penny — and the purchase token made a round trip through the
   * browser.
   *
   * Both are gone. Send the id; the server bills the row it wrote.
   */
  quoteId: string;
  carrierId: string;
  carrierServiceCode: string;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  currency: string;
}

/** Optional pickup add-on attached to a shipment. */
export interface PickupAddOn {
  pickupServiceId: string;
  scheduledDate: string;
  slotId: string;
  notes?: string;
  /**
   * ⚠️ No price. `pickupPriceCents` used to ride along here, and it was load-bearing
   * in a place nobody looks: credit redemption sized a customer's spending CAP from
   * it, so an inflated pickup price let a customer raise their own ceiling.
   *
   * Which TIER the customer picks is still theirs to choose. What that tier COSTS is
   * not. The server reads the price off the quote leg it wrote.
   *
   * (`bundleDiscountCents` is gone for the same reason — the server always
   * re-derived it, so sending it was decoration at best.)
   */
}

/** Response from POST /api/v2/pricing/get-quote. */
export interface QuoteResponse {
  success: boolean;
  data: {
    options: PricingOption[];
    addressesInServiceArea: boolean;
    correlationId: string;
    rejectionReasons?: QuoteRejection[];
    /** Phase B (#264): top-level customer-facing message when options is
     *  empty AND addressesInServiceArea is false. */
    reason?: string;
    /** Optional alternative action the customer can take. */
    suggestion?: string;
  };
}

export type QuotePackageIssue =
  | { packageNumber: number; kind: "sizeAndWeight" }
  | {
      packageNumber: number;
      kind: "weight" | "longestSide" | "lengthPlusGirth";
      actual: number;
      limit: number;
      unit: "kg" | "cm";
    };
export interface QuoteRejection {
  serviceId?: string;
  serviceName?: string;
  packageIssue?: QuotePackageIssue;
  carrierId: string;
  /** Machine code from the quote engine (e.g. "UNPRICED_LABEL_LANE"). */
  code: string;
  /** Human-readable, customer-facing reason. */
  reason: string;
  /** Optional alternative action. */
  suggestion?: string;
}

/**
 * ⚠️ `PricingDriftResponse` IS DELETED — the server cannot produce it.
 *
 * Drift meant "we recomputed your price, it moved >5%, confirm the new one." Every
 * word of that presupposes a price in the request. There isn't one (#533): the order
 * carries a quote id and the server bills the row it wrote.
 *
 * The only 409 this path produces now is `QUOTE_NOT_USABLE` — and the answer to it
 * is not "confirm this new price", it is "get a fresh quote."
 */

// Unified order creation request
export interface UnifiedOrderRequest {
  placerCustomerId?: string;

  // Payment
  paymentMethodType?: PaymentMethodType | null;
  paymentTermsId?: string;

  // Pay now with saved card - paymentMethodId presence implies charge
  paymentMethodId?: string; // Database ID of saved payment method to charge
  // Note: Credit is applied when paymentMethodType === "CREDIT"

  // Single shipment for customers
  shipments: [
    {
      // ── Pricing-redesign canonical shape ────────────────────────────────
      /** Required for new orders. Echoed from `PricingOption` returned by /get-quote. */
      selectedRate?: SelectedRate;
      /** Optional pickup add-on (booking is find-or-created server-side). */
      pickup?: PickupAddOn;
      /**
       * Re-submit flag: set true after server returned 409 PRICING_DRIFT and
       * the user confirmed new prices. Bypasses the 5% threshold for that shipment.
       */
      confirmRePricing?: boolean;

      // Sender
      senderName: string;
      senderEmail?: string;
      senderPhone?: string;
      /**
       * Phase 10a.5: when set, server reuses the entry's Address row so
       * PickupBooking dedup matches across orders. Mutually exclusive with
       * shipFrom — provide exactly one.
       */
      shipFromAddressBookEntryId?: string;
      shipFrom?: {
        street1: string;
        street2?: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
        latitude?: number;
        longitude?: number;
      };

      // Recipient
      recipientName: string;
      recipientEmail?: string;
      recipientPhone?: string;
      shipToAddressBookEntryId?: string;
      shipTo?: {
        street1: string;
        street2?: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
        latitude?: number;
        longitude?: number;
      };

      /**
       * Packages in THE CUSTOMER'S OWN UNITS — the same shape the quote
       * request takes. The server converts, once.
       *
       * ⚠️ Do not convert here. Sending pre-converted grams and centimetres is
       * what broke checkout: the quote carried pounds while the order carried
       * kilograms, so the server saw two different parcels and refused the
       * order as `wrong_shipment`.
       */
      packages: {
        weight: number;
        weightUnit: "g" | "kg" | "lb" | "oz";
        length: number;
        width: number;
        height: number;
        dimensionUnit: "cm" | "m" | "in" | "ft";
        packageKind?: "PARCEL" | "THICK_ENVELOPE";
        description?: string;
        isFragile?: boolean;
        requiresCooling?: boolean;
        declaredValueCents?: number;
      }[];

      // Notes
      deliveryNotes?: string;
    },
  ];
}

// Unified order creation response
// Server returns the Order object directly with shipments nested inside
export type UnifiedOrderResponse = Order;

// List response wrapper
export interface ListResponse<T> {
  data: T[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/** Phase 13 (#203): single window per day, "HH:MM" 24h format. close may
 *  exceed "24:00" for overnight wrap (e.g. "27:00" = 03:00 next day). */
export interface DayHours {
  open: string;
  close: string;
}
export interface WeeklyHours {
  mon: DayHours | null;
  tue: DayHours | null;
  wed: DayHours | null;
  thu: DayHours | null;
  fri: DayHours | null;
  sat: DayHours | null;
  sun: DayHours | null;
}

export interface CarrierDropoffLocation {
  id: string;
  carrierId: string;
  name: string;
  address: {
    line1: string;
    line2: string | null;
    city: string;
    province: string;
    country: string;
    postalCode: string | null;
    location?: { lat: number; lng: number } | null;
    /** Phase 13 (#203): IANA timezone for the location's local hours. */
    timezone?: string | null;
  };
  /** Phase 13 (#203): typed schedule. Null when unset or malformed. */
  operatingHours: WeeklyHours | null;
  distanceKm?: number;
  /** Phase 13 (#203): server-computed open/closed status in location's TZ. */
  openNow: boolean;
  /** "HH:MM" — today's close, when openNow=true. */
  closesAtLocal: string | null;
  /** "HH:MM" same-day, "tomorrow HH:MM", or "Tuesday HH:MM" — when openNow=false. */
  opensAtLocal: string | null;
}
